import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Paper,
  useMediaQuery,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useParams, useNavigate } from "react-router-dom";
import { dataGridCustomizations } from "../../../theme/customizations";
import AppTheme from "../../../theme/AppTheme";
import { CssBaseline } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCampaignById, updateCouponStatus } from "../../../service/BuyerConfiguration/CampaignManagement/campaignService";
import { notifyError, notifySuccess } from "../../../components/ToastContainer/Toast/Toast";
import GenericModal from "../../../components/Modal/GenericModal";
import GenerateCouponsModal from "./Fragments/GenerateCouponsModal";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { formatCouponTypeLabel } from "./utils/couponTypeLabel";

const ViewCampaign = (props: { disableCustomTheme?: boolean }) => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState<any>(null);
  const [openGenerateCouponsModal, setOpenGenerateCouponsModal] = useState(false);
  const [updatingCouponId, setUpdatingCouponId] = useState<string | number | null>(null);
  const [couponPaginationModel, setCouponPaginationModel] = useState({ page: 0, pageSize: 10 });
  const xThemeComponents = {
    ...dataGridCustomizations,
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const canGenerateCoupons = useMemo(() => {
    const status = (campaign?.status || "").toString().trim().toLowerCase();
    return status === "draft" || status === "active" || status === "paused";
  }, [campaign?.status]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getOrdinalSuffix = (day: number): string => {
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const formatDateOnly = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    const day = date.getDate();
    const suffix = getOrdinalSuffix(day);
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day}${suffix} ${month} ${year}`;
  };

  const fetchCampaign = useCallback(async (opts?: { silent?: boolean }) => {
    if (!campaignId) return;
    const silent = Boolean(opts?.silent);
    try {
      if (!silent) setLoading(true);
      const response = await getCampaignById(campaignId);
      const campaignData =
        response?.data?.campaign ||
        response?.data?.data ||
        response?.data ||
        response?.campaign ||
        null;
      setCampaign(campaignData);
    } catch (error: any) {
      notifyError(error?.response?.data?.message || "Failed to load campaign details");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [campaignId]);

  const handleCouponStatusChange = useCallback(
    async (couponId: string | number, nextStatus: "active" | "inactive") => {
      try {
        setUpdatingCouponId(couponId);
        await updateCouponStatus(couponId, nextStatus);
        notifySuccess("Coupon status updated");
        await fetchCampaign({ silent: true });
      } catch (e: any) {
        notifyError(e?.response?.data?.message || "Failed to update coupon status");
      } finally {
        setUpdatingCouponId(null);
      }
    },
    [fetchCampaign]
  );

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const coupons = useMemo(() => {
    const list = campaign?.coupons;
    return Array.isArray(list) ? list : [];
  }, [campaign?.coupons]);

  useEffect(() => {
    const pageSize = couponPaginationModel.pageSize;
    const maxPage = Math.max(0, Math.ceil(coupons.length / pageSize) - 1);
    if (couponPaginationModel.page > maxPage) {
      setCouponPaginationModel((m) => ({ ...m, page: maxPage }));
    }
  }, [coupons.length, couponPaginationModel.page, couponPaginationModel.pageSize]);

  const couponColumns = useMemo<GridColDef[]>(
    () => [
      { field: "code", headerName: "Code", minWidth: 160, flex: 1.2 },
      {
        field: "type",
        headerName: "Type",
        minWidth: 120,
        flex: 0.8,
        valueGetter: (_value: any, row: any) => formatCouponTypeLabel(row?.type),
      },
      {
        field: "max_discount_amount",
        headerName: "Max Discount Amount",
        minWidth: 150,
        flex: 0.9,
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 190,
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params.row as any;
          const raw = (row?.status ?? params.value ?? "").toString();
          const normalized = raw.trim().toLowerCase();
          const isActive = normalized === "active";
          const label = raw
            ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
            : "-";
          const couponId = row?.id;
          const selectValue: "active" | "inactive" =
            normalized === "inactive" ? "inactive" : "active";
          const isUpdating = updatingCouponId !== null && String(updatingCouponId) === String(couponId);

          /** Only Active/Inactive can be toggled via API; terminal states use a read-only badge. */
          const showStatusDropdown = normalized === "active" || normalized === "inactive";

          const statusBadgeColor =
            normalized === "expired"
              ? "warning"
              : normalized === "revoked"
                ? "default"
                : isActive
                  ? "success"
                  : "default";

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {showStatusDropdown ? (
                <Select
                  size="small"
                  value={selectValue}
                  disabled={!couponId || isUpdating}
                  onChange={(e) => {
                    const v = (e.target.value || "").toString() as "active" | "inactive";
                    if (!couponId || v === selectValue) return;
                    handleCouponStatusChange(couponId, v);
                  }}
                  variant="outlined"
                  displayEmpty
                  renderValue={() => label}
                  sx={{
                    height: 26,
                    minWidth: 110,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    ...(isActive
                      ? {
                          color: (theme) => theme.palette.success.main,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: (theme) => theme.palette.success.main,
                          },
                        }
                      : {
                          color: (theme) => theme.palette.text.secondary,
                          backgroundColor: (theme) => theme.palette.action.hover,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: (theme) => theme.palette.divider,
                          },
                        }),
                    "& .MuiSelect-select": {
                      py: 0,
                      pl: 1.1,
                      pr: 3,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSvgIcon-root": { right: 8 },
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              ) : (
                <Chip
                  label={label}
                  size="small"
                  color={statusBadgeColor}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "end_at",
        headerName: "End At",
        minWidth: 170,
        flex: 1,
        valueGetter: (_value: any, row: any) => formatDateOnly(row?.end_at),
      },
      {
        field: "actions",
        headerName: "Action",
        minWidth: 90,
        flex: 0.5,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params.row as { id?: string | number };
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                aria-label="View coupon"
                disabled={!row?.id || !campaignId}
                onClick={() => {
                  if (row?.id && campaignId) {
                    navigate(`/campaign-management/${campaignId}/coupons/${row.id}`);
                  }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [campaignId, formatDateOnly, handleCouponStatusChange, navigate, updatingCouponId]
  );

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              width: "100%",
              flexDirection: { xs: "column", sm: "row" },
              rowGap: 1.5,
            }}
          >
            <Typography component="h2" variant="h6">
              View Campaign
            </Typography>
            {canGenerateCoupons ? (
              <Button
                variant="contained"
                sx={{ minWidth: { xs: "100%", sm: "auto" } }}
                onClick={() => setOpenGenerateCouponsModal(true)}
              >
                Generate Coupons
              </Button>
            ) : null}
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : campaign ? (
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: "none",
              }}
            >
              {isMobile ? (
                <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1.25 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Title
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {campaign?.title || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Campaign Key
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {campaign?.campaign_key || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {campaign?.description || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Created By
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {campaign?.created_by || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Status
                    </Typography>
                    <Chip
                      label={
                        campaign?.status
                          ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1).toLowerCase()
                          : "-"
                      }
                      size="small"
                      color={(campaign?.status || "").toLowerCase() === "active" ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Created At
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {formatDateTime(campaign?.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", columnGap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Updated At
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right", maxWidth: "60%", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                      {formatDateTime(campaign?.updated_at)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 4 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "140px 1fr", sm: "170px 1fr" },
                        columnGap: 2,
                        rowGap: 1.25,
                        alignItems: "start",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Title
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {campaign?.title || "-"}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Campaign Key
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {campaign?.campaign_key || "-"}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Description
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {campaign?.description || "-"}
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Created By
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {campaign?.created_by || "-"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flexShrink: 0, minWidth: { xs: "auto", sm: 260 } }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Chip
                        label={
                          campaign?.status
                            ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1).toLowerCase()
                            : "-"
                        }
                        size="small"
                        color={(campaign?.status || "").toLowerCase() === "active" ? "success" : "default"}
                        variant="outlined"
                      />
                    </Box>

                    <Box
                      sx={{
                        mt: 2,
                        display: "grid",
                        gridTemplateColumns: "110px 1fr",
                        columnGap: 2,
                        rowGap: 1.25,
                        alignItems: "start",
                        justifyItems: "start",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Created At
                      </Typography>
                      <Typography variant="body2">{formatDateTime(campaign?.created_at)}</Typography>

                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Updated At
                      </Typography>
                      <Typography variant="body2">{formatDateTime(campaign?.updated_at)}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>

            <Box sx={{ mt: 2 }}>
              {coupons.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    There are no coupons.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Box sx={{ minWidth: 720 }}>
                    <DataGrid
                      rows={coupons}
                      columns={couponColumns}
                      getRowId={(row) => row.id}
                      disableColumnMenu
                      pageSizeOptions={[10]}
                      paginationModel={couponPaginationModel}
                      onPaginationModelChange={setCouponPaginationModel}
                      sx={{
                        height: { xs: 500, sm: 520 },
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: (theme) => theme.palette.background.paper,
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Campaign details not found.
          </Typography>
        )}

        <GenericModal
          open={openGenerateCouponsModal}
          onClose={() => setOpenGenerateCouponsModal(false)}
          title="Generate Coupons"
          maxWidth="sm"
        >
          {openGenerateCouponsModal ? (
            <GenerateCouponsModal
              campaignId={campaignId}
              onClose={() => setOpenGenerateCouponsModal(false)}
              onGenerated={() => {
                fetchCampaign({ silent: true });
              }}
            />
          ) : null}
        </GenericModal>
      </Box>
    </AppTheme>
  );
};

export default ViewCampaign;

